import UserGuide from '@/components/help/UserGuide';
import { useEffect, useRef, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { Loader2, X } from 'lucide-react';
import { useUpdateSalePayment } from '@/hooks/useSales';
import {
  salePaymentDraftSchema,
  paymentBalance,
  paymentRequestKey,
  type SalePaymentDraft,
} from '@klothiq/validation';
import { PaymentFields } from './SalePaymentsEditor';
import { toast } from 'sonner';

export interface PaymentItem {
  id: string;
  amount: number;
  payment_method: string;
  payment_date: string;
  payment_type?: string;
  account_id?: string;
  reference_number?: string | null;
  notes?: string | null;
  is_void?: boolean;
  updated_at?: string;
  cash_accounts?: { id: string; name: string } | null;
}
export interface EditPaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  payment: PaymentItem | null;
  saleId: string;
  saleNumber?: string;
  accounts: Array<{ id: string; name: string; account_type?: string; current_balance?: number }>;
  totalAmount?: number;
  paidAmount?: number;
  onSuccess?: () => void;
}

export default function EditPaymentModal({
  isOpen,
  onClose,
  payment,
  saleId,
  saleNumber,
  accounts,
  totalAmount,
  paidAmount,
  onSuccess,
}: EditPaymentModalProps) {
  const update = useUpdateSalePayment();
  const [draft, setDraft] = useState<SalePaymentDraft | null>(null);
  const [error, setError] = useState('');
  const request = useRef({ signature: '', key: '' });
  useEffect(() => {
    if (isOpen && payment) {
      setDraft({
        amount: Number(payment.amount),
        account_id: payment.account_id ?? payment.cash_accounts?.id ?? '',
        payment_method: payment.payment_method as SalePaymentDraft['payment_method'],
        payment_date: payment.payment_date,
        reference_number: payment.reference_number ?? '',
        notes: payment.notes ?? '',
        reason: '',
      });
      setError('');
      request.current = { signature: '', key: '' };
    }
  }, [isOpen, payment?.id]);
  return (
    <Dialog.Root
      open={isOpen}
      onOpenChange={(open) => {
        if (!open && !update.isPending) onClose();
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/50" data-field-guide-scope />
        <Dialog.Content className="obsidian-panel fixed left-1/2 top-1/2 z-50 max-h-[90dvh] w-[calc(100%-2rem)] max-w-xl -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-xl border bg-card p-5 shadow-xl">
          <div className="flex items-start justify-between gap-3 pr-8">
            <Dialog.Title className="pr-10 text-lg font-semibold">
              Correct payment {saleNumber ? `· ${saleNumber}` : ''}
            </Dialog.Title>
            <UserGuide compact />
          </div>
          <Dialog.Description className="mt-1 text-sm text-muted-foreground">
            Update this recorded entry. The linked cash transaction and sale balance will be
            corrected together.
          </Dialog.Description>
          <Dialog.Close
            disabled={update.isPending}
            aria-label="Close payment editor"
            className="absolute right-3 top-3 flex h-11 w-11 items-center justify-center rounded-md hover:bg-accent"
          >
            <X className="h-4 w-4" />
          </Dialog.Close>
          {draft && payment && (
            <form
              className="mt-5 space-y-4"
              noValidate
              onSubmit={async (event) => {
                event.preventDefault();
                setError('');
                const checked = salePaymentDraftSchema.safeParse(draft);
                if (!checked.success) {
                  setError(checked.error.issues[0]?.message ?? 'Check payment details');
                  return;
                }
                if (
                  (draft.amount !== Number(payment.amount) ||
                    draft.account_id !== payment.account_id) &&
                  (checked.data.reason?.length ?? 0) < 3
                ) {
                  setError('Enter a correction reason of at least 3 characters.');
                  return;
                }
                const isRefund = payment.payment_type === 'refund';
                if (paidAmount !== undefined && totalAmount !== undefined) {
                  const nextPaid =
                    paidAmount + (draft.amount - Number(payment.amount)) * (isRefund ? -1 : 1);
                  if (nextPaid < 0 || nextPaid > totalAmount) {
                    setError(
                      isRefund
                        ? 'The refund correction exceeds the collected amount.'
                        : `Maximum for this entry is ₹${paymentBalance(totalAmount, paidAmount, Number(payment.amount)).toFixed(2)}.`,
                    );
                    return;
                  }
                }
                const payload = {
                  ...checked.data,
                  sale_id: saleId,
                  payment_id: payment.id,
                  expected_updated_at: payment.updated_at,
                };
                const signature = JSON.stringify(payload);
                if (signature !== request.current.signature)
                  request.current = { signature, key: paymentRequestKey() };
                try {
                  await update.mutateAsync({ ...payload, request_key: request.current.key });
                  toast.success('Recorded payment corrected');
                  onSuccess?.();
                  onClose();
                } catch (err) {
                  setError(err instanceof Error ? err.message : 'Unable to correct payment');
                }
              }}
            >
              <PaymentFields
                prefix="correct-payment"
                value={draft}
                onChange={setDraft}
                accounts={accounts}
                reason
                disabled={update.isPending}
              />
              {error && (
                <p
                  role="alert"
                  className="rounded-md bg-destructive/10 p-3 text-sm text-destructive"
                >
                  {error}
                </p>
              )}
              <button
                disabled={update.isPending || payment.is_void}
                className="flex min-h-11 w-full items-center justify-center gap-2 rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground disabled:opacity-50"
              >
                {update.isPending && <Loader2 className="h-4 w-4 animate-spin" />}Save correction
              </button>
            </form>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
